import React from 'react';

type Breakpoint = 'desktop' | 'tablet' | 'mobile';

type SizeRect = {
    top: number;
    left: number;
    width: number;
    height: number;
};

type ItemLayout = Partial<Record<Breakpoint, SizeRect>>;

type BoardItem = {
    id: string;
    content: React.ReactNode;
    layout: ItemLayout;
};

type ResponsiveGridBoardProps = {
    items: BoardItem[];
    gap?: number;
    snapTolerance?: number;
    breakpointWidths?: {
        desktop: number;
        tablet: number
    };
    className?: string;
    style?: React.CSSProperties;
};

const useBreakpoint = (bw: {
    desktop: number;
    tablet: number
}): Breakpoint => {
    const get = () => {
        const w = typeof window !== 'undefined' ? window.innerWidth : bw.desktop;
        if (w >= bw.desktop) {
            return 'desktop';
        }
        if (w >= bw.tablet) {
            return 'tablet';
        }
        return 'mobile';
    };
    const [ bp, setBp ] = React.useState<Breakpoint>(get());
    React.useEffect(() => {
        const onResize = () => setBp(get());
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ bw.desktop, bw.tablet ]);
    return bp;
};

const pickSize = (il: ItemLayout, bp: Breakpoint): SizeRect | undefined => {
    if (bp === 'desktop') {
        return il.desktop ?? il.tablet ?? il.mobile;
    }
    return il.tablet ?? il.desktop ?? il.mobile;
};

const clusterEdges = (values: number[], tolerance: number): number[] => {
    const sorted = [ ...values ].sort((a, b) => a - b);
    const clusters: { sum: number; count: number }[] = [];
    sorted.forEach(value => {
        const last = clusters[clusters.length - 1];
        if (last && value - last.sum / last.count <= tolerance) {
            last.sum += value;
            last.count += 1;
        } else {
            clusters.push({ sum: value, count: 1 });
        }
    });
    return clusters.map(c => c.sum / c.count);
};

const nearestLineIndex = (lines: number[], value: number): number => {
    let bestIndex = 0;
    let bestDistance = Infinity;
    lines.forEach((line, index) => {
        const distance = Math.abs(line - value);
        if (distance < bestDistance) {
            bestDistance = distance;
            bestIndex = index;
        }
    });
    return bestIndex;
};

const GridBoard: React.FC<ResponsiveGridBoardProps> = ({
    items,
    gap = 16,
    snapTolerance = 20,
    breakpointWidths = { desktop: 1200, tablet: 768 },
    className,
    style,
}) => {
    const bp = useBreakpoint(breakpointWidths);

    const placed = React.useMemo(() => {
        return items
        .map(item => {
            const size = pickSize(item.layout, bp === 'mobile' ? 'tablet' : bp);
            if (!size || !size.width || !size.height) {
                return null;
            }
            return { id: item.id, content: item.content, size };
        })
        .filter(Boolean) as { id: string; content: React.ReactNode; size: SizeRect }[];
    }, [ items, bp ]);

    if (placed.length === 0) {
        return null;
    }

    if (bp === 'mobile') {
        const ordered = [ ...placed ].sort((a, b) => a.size.top - b.size.top);
        return (
            <div
                className={className}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap,
                    width: '100%',
                    ...style,
                }}
            >
                {ordered.map(({ id, content }) => (
                    <div key={id} style={{ width: '100%' }}>
                        {content}
                    </div>
                ))}
            </div>
        );
    }

    const columnLines = clusterEdges(
        placed.flatMap(p => [ p.size.left, p.size.left + p.size.width ]),
        snapTolerance,
    );
    const rowLines = clusterEdges(
        placed.flatMap(p => [ p.size.top, p.size.top + p.size.height ]),
        snapTolerance,
    );

    const columnTracks = columnLines.slice(1).map((line, i) => line - columnLines[i]);
    const rowTracks = rowLines.slice(1).map((line, i) => line - rowLines[i]);

    return (
        <div
            className={className}
            style={{
                display: 'grid',
                gridTemplateColumns: columnTracks.map(w => `${w}fr`).join(' '),
                gridTemplateRows: rowTracks.map(h => `${h}px`).join(' '),
                gap,
                width: '100%',
                ...style,
            }}
        >
            {placed.map(({ id, content, size }) => {
                const colStart = nearestLineIndex(columnLines, size.left) + 1;
                const colEnd = nearestLineIndex(columnLines, size.left + size.width) + 1;
                const rowStart = nearestLineIndex(rowLines, size.top) + 1;
                const rowEnd = nearestLineIndex(rowLines, size.top + size.height) + 1;

                return (
                    <div
                        key={id}
                        style={{
                            gridColumn: `${colStart} / ${Math.max(colEnd, colStart + 1)}`,
                            gridRow: `${rowStart} / ${Math.max(rowEnd, rowStart + 1)}`,
                            minWidth: 0,
                            minHeight: 0,
                        }}
                    >
                        {content}
                    </div>
                );
            })}
        </div>
    );
};

export default GridBoard;
